import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import {
  BOARD_CELLS,
  BOARD_SIZE,
  cellIndex,
  indexOfX,
  indexOfY,
  indexOfZ,
} from '../game/board';
import { BLACK_PROBABILITY } from '../game/stones';
import type { Game } from '../game/game';
import type { Stone, StoneColor, StoneType } from '../game/types';
import {
  FACE_INFO,
  isCutawayCell,
  sectionCameraPose,
  sectionDepthFromOffset,
  sectionFixedValue,
  snapDepth,
  type Face,
} from '../ui/crossSection';
import { SCENE_THEMES, type ThemeName } from '../ui/theme';

/** マス間隔（3D 座標上の 1 マスの長さ） */
const SPACING = 1;
/** 石の半径 */
const STONE_RADIUS = 0.32;
/** ピッキング用の（見えない）ボックスの 1 辺の長さ */
const PICK_BOX = 0.96;
/** 盤面中心からのオフセット（0..9 を -4.5..4.5 に変換） */
const HALF = (BOARD_SIZE - 1) / 2;
/** 断面面のドラッグ開始とクリックを区切る移動距離のしきい値（px） */
const SECTION_DRAG_THRESHOLD = 8;
/** 断面面の不透明度（通常 / ドラッグ中） */
const SECTION_OPACITY = 0.14;
const SECTION_DRAG_OPACITY = 0.4;

// 石の色（黒/白）はテーマに依存しない「石そのものの色」
const COLOR_BLACK = new THREE.Color(0x141414);
const COLOR_WHITE = new THREE.Color(0xf5f5f5);
// テーマ依存の色（背景・グリッド・量子殻・断面ハイライト）は SCENE_THEMES を参照する
/** グリッドの単位区間の伸びる 3 方向 */
const GRID_DIRS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
];

function cellPosition(x: number, y: number, z: number): THREE.Vector3 {
  return new THREE.Vector3(
    (x - HALF) * SPACING,
    (y - HALF) * SPACING,
    (z - HALF) * SPACING,
  );
}

/**
 * 1 つの石を表示するメッシュ群（本体の球＋量子状態を示すワイヤーフレームの殻）。
 * 殻は観測確定（黒/白に固定）すると非表示になる。
 */
class StoneMesh {
  readonly group: THREE.Group;
  private readonly body: THREE.Mesh<THREE.SphereGeometry, THREE.MeshStandardMaterial>;
  private readonly shell: THREE.Mesh<THREE.IcosahedronGeometry, THREE.MeshBasicMaterial>;

  constructor(position: THREE.Vector3, shellColor: number, shellOpacity: number) {
    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.35,
      metalness: 0.05,
    });
    this.body = new THREE.Mesh(new THREE.SphereGeometry(STONE_RADIUS, 24, 24), bodyMat);

    const shellMat = new THREE.MeshBasicMaterial({
      color: shellColor,
      wireframe: true,
      transparent: true,
      opacity: shellOpacity,
    });
    this.shell = new THREE.Mesh(
      new THREE.IcosahedronGeometry(STONE_RADIUS * 1.22, 1),
      shellMat,
    );

    this.group = new THREE.Group();
    this.group.add(this.body);
    this.group.add(this.shell);
    this.group.position.copy(position);
  }

  /** 量子殻の色・不透明度を更新する（テーマ切替用。石の種類・観測状態は変わらない） */
  setShellColor(color: number, opacity: number): void {
    const mat = this.shell.material;
    mat.color.set(color);
    mat.opacity = opacity;
  }

  /** 石の種類と観測状態に応じて見た目を更新する */
  update(type: StoneType, observed: StoneColor | null): void {
    const mat = this.body.material;
    if (observed !== null) {
      mat.color.copy(observed === 'black' ? COLOR_BLACK : COLOR_WHITE);
      mat.roughness = 0.3;
      this.shell.visible = false;
    } else {
      // 未観測（量子）: 黒確率が高いほど暗い灰色にする
      const p = BLACK_PROBABILITY[type];
      mat.color.copy(COLOR_WHITE).lerp(COLOR_BLACK, p);
      mat.roughness = 0.5;
      this.shell.visible = true;
    }
    mat.needsUpdate = true;
  }

  dispose(): void {
    this.body.geometry.dispose();
    this.body.material.dispose();
    this.shell.geometry.dispose();
    this.shell.material.dispose();
  }
}

/**
 * 盤面を 3D で表示する Three.js シーン。
 * ゲームロジック（Game）の状態を毎手同步して描画し、
 * マスクリック（石の配置）の raycasting も担当する。
 * 断面面をドラッグして深さを変更する操作も担当する（表示専用。
 * スライダー側と同一の深さ状態を更新するため、両者は常に同期する）。
 */
export class BoardScene {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly controls: OrbitControls;

  private readonly stoneMeshes = new Map<number, StoneMesh>();
  private readonly pickBoxes: THREE.Mesh[] = [];
  private activePickBoxes: THREE.Mesh[] = [];
  private readonly hoverBox: THREE.Mesh<THREE.BoxGeometry, THREE.MeshBasicMaterial>;
  private readonly highlightGroup: THREE.Group;
  private readonly sectionGroup: THREE.Group;
  private gridLines: THREE.LineSegments | null = null;

  /** 断面（切り出し）表示の状態。表示専用でゲーム状態には影響しない */
  private cutaway: { face: Face; depth: number } | null = null;
  /** sync() での直近の盤面スナップショット（切り出し時のピッキング再計算に使う） */
  private boardSnapshot: (Stone | null)[] = [];
  /** 現在のテーマ（背景・グリッド・量子殻・断面ハイライトの色を決定する） */
  private theme: ThemeName;

  /** 断面の半透明面（ドラッグのヒット判定に使う） */
  private sectionPlane: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> | null = null;
  /** 断面の枠線（ドラッグ中の強調表示に使う） */
  private sectionBorder: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial> | null = null;
  /**
   * 断面面のドラッグ状態。
   * armed: 面の上に押し込んだがしきい値未満（まだクリックの可能性）。
   * active: しきい値を超え、面をドラッグ中。
   */
  private sectionDrag:
    | {
        phase: 'armed' | 'active';
        startClient: { x: number; y: number };
        startDepth: number;
        /** ドラッグ開始時の面上の掴み点 */
        grabPoint: THREE.Vector3;
        /** ドラッグ開始時の画面平行参照平面（掴み点を通る。面自体の移動に影響されない） */
        refPlane: THREE.Plane;
        /** 直前に通知した深さ（重複通知の抑制） */
        lastDepth: number;
      }
    | null = null;
  /** pointerdown で記録したピッキング対象（クリックの場合のみ pointerup で配置） */
  private pendingPick: number | null = null;
  /** pointerdown 時点のクライアント座標（クリックとカメラ回転の区別に使う） */
  private downClient: { x: number; y: number } | null = null;

  private readonly defaultCameraPos = new THREE.Vector3(9.5, 7.5, 11.5);
  private readonly defaultTarget = new THREE.Vector3(0, 0, 0);

  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly resizeObserver: ResizeObserver;
  private hoverEnabled = true;
  private disposed = false;

  /** マス（空き）がクリックされたときのコールバック */
  onCellPick: ((x: number, y: number, z: number) => void) | null = null;
  /** 断面面のドラッグで深さが変化したときのコールバック（表示専用） */
  onSectionDepthChange: ((depth: number) => void) | null = null;

  constructor(private readonly container: HTMLElement, theme: ThemeName = 'dark') {
    this.theme = theme;

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(SCENE_THEMES[theme].bg);

    const aspect = container.clientWidth / Math.max(1, container.clientHeight);
    this.camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 200);
    this.camera.position.copy(this.defaultCameraPos);

    // OrbitControls より先にポインター処理を登録する。
    // 断面面のドラッグ開始時に stopImmediatePropagation() でカメラ回転を先回りで止められるようにする
    this.renderer.domElement.addEventListener('pointerdown', this.handlePointerDown);
    this.renderer.domElement.addEventListener('pointermove', this.handlePointerMove);
    this.renderer.domElement.addEventListener('pointerup', this.handlePointerUp);
    this.renderer.domElement.addEventListener('pointercancel', this.handlePointerUp);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.copy(this.defaultTarget);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minDistance = 4;
    this.controls.maxDistance = 40;

    this.addLights();
    this.rebuildGrid();

    // ピッキング用の見えないボックス（全 1000 マス分）
    const pickGeo = new THREE.BoxGeometry(PICK_BOX, PICK_BOX, PICK_BOX);
    const pickMat = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false,
      colorWrite: false,
    });
    for (let z = 0; z < BOARD_SIZE; z++) {
      for (let y = 0; y < BOARD_SIZE; y++) {
        for (let x = 0; x < BOARD_SIZE; x++) {
          const box = new THREE.Mesh(pickGeo, pickMat);
          box.position.copy(cellPosition(x, y, z));
          box.userData.index = cellIndex(x, y, z);
          this.scene.add(box);
          this.pickBoxes.push(box);
        }
      }
    }

    // ホバーハイライト（半透明ボックス）
    const hoverMat = new THREE.MeshBasicMaterial({
      color: 0x4da3ff,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
    });
    this.hoverBox = new THREE.Mesh(new THREE.BoxGeometry(0.98, 0.98, 0.98), hoverMat);
    this.hoverBox.visible = false;
    this.scene.add(this.hoverBox);

    this.highlightGroup = new THREE.Group();
    this.scene.add(this.highlightGroup);

    // 断面のハイライト（半透明面＋枠線）
    this.sectionGroup = new THREE.Group();
    this.sectionGroup.visible = false;
    this.scene.add(this.sectionGroup);

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(container);

    this.renderer.setAnimationLoop(this.animate);
  }

  private addLights(): void {
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.65));
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(6, 10, 8);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0x88aaff, 0.35);
    fill.position.set(-8, -4, -6);
    this.scene.add(fill);
  }

  /**
   * 盤面グリッド（格子線）を再構築する。
   * 切り出し表示中は、両端が切り出し領域内にある単位区間のみを省略する。
   * （断面境界の線は残し、切断面がどこにあるか分かるようにする）
   */
  private rebuildGrid(): void {
    if (this.gridLines !== null) {
      this.scene.remove(this.gridLines);
      this.gridLines.geometry.dispose();
      (this.gridLines.material as THREE.Material).dispose();
      this.gridLines = null;
    }
    const positions: number[] = [];
    const push = (x: number, y: number, z: number) =>
      positions.push((x - HALF) * SPACING, (y - HALF) * SPACING, (z - HALF) * SPACING);
    const hidden = (x: number, y: number, z: number): boolean =>
      this.cutaway !== null && isCutawayCell(this.cutaway.face, this.cutaway.depth, x, y, z);
    for (let x = 0; x < BOARD_SIZE; x++) {
      for (let y = 0; y < BOARD_SIZE; y++) {
        for (let z = 0; z < BOARD_SIZE; z++) {
          for (const [dx, dy, dz] of GRID_DIRS) {
            const nx = x + dx;
            const ny = y + dy;
            const nz = z + dz;
            if (nx >= BOARD_SIZE || ny >= BOARD_SIZE || nz >= BOARD_SIZE) continue;
            if (hidden(x, y, z) && hidden(nx, ny, nz)) continue;
            push(x, y, z);
            push(nx, ny, nz);
          }
        }
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const palette = SCENE_THEMES[this.theme];
    const mat = new THREE.LineBasicMaterial({
      color: palette.grid,
      transparent: true,
      opacity: palette.gridOpacity,
    });
    this.gridLines = new THREE.LineSegments(geo, mat);
    this.scene.add(this.gridLines);
  }

  /**
   * テーマ（ダーク/ライト）を切り替える。
   * 背景・グリッド・量子殻・断面ハイライトの色を更新する。
   * カメラ（視点）とゲーム状態は変更しない。
   */
  setTheme(theme: ThemeName): void {
    if (theme === this.theme) return;
    this.theme = theme;
    (this.scene.background as THREE.Color).set(SCENE_THEMES[theme].bg);
    const { shell, shellOpacity } = SCENE_THEMES[theme];
    for (const mesh of this.stoneMeshes.values()) {
      mesh.setShellColor(shell, shellOpacity);
    }
    this.rebuildGrid();
    if (this.cutaway !== null) this.updateSectionHighlight();
  }

  private handleResize = (): void => {
    const w = this.container.clientWidth;
    const h = Math.max(1, this.container.clientHeight);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  };

  private updatePointerFromEvent(event: PointerEvent): void {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private handlePointerMove = (event: PointerEvent): void => {
    this.updatePointerFromEvent(event);
    if (this.sectionDrag !== null) {
      this.updateSectionDrag(event);
      return;
    }
    this.updateHover();
  };

  private handlePointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    this.updatePointerFromEvent(event);
    this.downClient = { x: event.clientX, y: event.clientY };
    this.pendingPick = this.hoverEnabled ? this.pickAtPointer() : null;

    // 断面面の上ならドラッグ開始の候補にする。OrbitControls より先に動くため
    // stopImmediatePropagation() でカメラ回転を止め、移動距離のしきい値で
    // クリック（石配置）とドラッグ（深さ変更）を区別する
    if (this.cutaway !== null) {
      const hit = this.sectionHitAtPointer();
      if (hit !== null) {
        this.hoverBox.visible = false;
        this.sectionDrag = {
          phase: 'armed',
          startClient: { x: event.clientX, y: event.clientY },
          startDepth: this.cutaway.depth,
          grabPoint: hit,
          // 参照平面は断面面そのものではなく、カメラの視線に垂直（画面と平行）で
          // 掴み点を通る面を使う。ポインタの画面空間上の移動が 3D 上の移動と
          // 1:1 で対応するため、どのカメラ方向からも法線方向へドラッグできる
          refPlane: this.screenParallelPlaneAt(hit),
          lastDepth: this.cutaway.depth,
        };
        event.stopImmediatePropagation();
        this.renderer.domElement.setPointerCapture(event.pointerId);
      }
    }
  };

  private handlePointerUp = (event: PointerEvent): void => {
    if (this.sectionDrag !== null) {
      const wasClick = this.sectionDrag.phase === 'armed';
      this.sectionDrag = null;
      this.downClient = null;
      this.setSectionDragVisual(false);
      if (this.renderer.domElement.hasPointerCapture(event.pointerId)) {
        this.renderer.domElement.releasePointerCapture(event.pointerId);
      }
      // pointerdown で OrbitControls を止めたので、こちらも止めておく
      event.stopImmediatePropagation();
      // 面の上でのクリック（ドラッグなし）は通常どおり石を置ける
      if (wasClick) this.firePendingPick();
      this.pendingPick = null;
      return;
    }
    if (this.downClient === null) return;
    const moved = Math.hypot(
      event.clientX - this.downClient.x,
      event.clientY - this.downClient.y,
    );
    this.downClient = null;
    // しきい値以上の移動はカメラ回転/パンなので石を置かない
    if (moved > SECTION_DRAG_THRESHOLD) {
      this.pendingPick = null;
      return;
    }
    this.firePendingPick();
    this.pendingPick = null;
  };

  /** 現在のポインタが断面面上にあるとき、その上の点を返す（なければ null） */
  private sectionHitAtPointer(): THREE.Vector3 | null {
    if (this.sectionPlane === null) return null;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObject(this.sectionPlane, false);
    return hits.length > 0 ? hits[0].point.clone() : null;
  }

  /**
   * `point` を通る、カメラの視線に垂直な（画面と平行な）平面を作る。
   * ポインタのレイとこの面の交点は、ポインタの移動量をそのまま 3D に反映する。
   * その交点の移動を内側法線方向に射影した分だけ断面を動かすことで、
   * 断面面そのものとの交点（常に法線方向の成分が 0 になる）では深さが
   * 動かない問題を避ける。
   */
  private screenParallelPlaneAt(point: THREE.Vector3): THREE.Plane {
    const normal = new THREE.Vector3();
    this.camera.getWorldDirection(normal);
    return new THREE.Plane().setFromNormalAndCoplanarPoint(normal, point);
  }

  /** 断面ドラッグ中の更新: ポインタ位置から深さを求めて（整数に丸めて）通知する */
  private updateSectionDrag(event: PointerEvent): void {
    const drag = this.sectionDrag;
    const cutaway = this.cutaway;
    if (drag === null || cutaway === null) return;
    if (drag.phase === 'armed') {
      const moved = Math.hypot(
        event.clientX - drag.startClient.x,
        event.clientY - drag.startClient.y,
      );
      if (moved <= SECTION_DRAG_THRESHOLD) return;
      drag.phase = 'active';
      this.setSectionDragVisual(true);
    }
    // ドラッグ開始時点の画面平行参照平面と交差させる
    // （面自体が移動しても、この面と掴み点は固定なので計算が安定する）
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const point = new THREE.Vector3();
    if (this.raycaster.ray.intersectPlane(drag.refPlane, point) === null) return;
    const offset: [number, number, number] = [
      point.x - drag.grabPoint.x,
      point.y - drag.grabPoint.y,
      point.z - drag.grabPoint.z,
    ];
    const depth = snapDepth(sectionDepthFromOffset(cutaway.face, drag.startDepth, offset));
    if (depth !== drag.lastDepth) {
      drag.lastDepth = depth;
      this.onSectionDepthChange?.(depth);
    }
  }

  /** 記録したピッキング対象（空きマス）を配置コールバックで発火する */
  private firePendingPick(): void {
    const index = this.pendingPick;
    if (index !== null && this.onCellPick) {
      this.onCellPick(indexOfX(index), indexOfY(index), indexOfZ(index));
    }
  }

  /** 断面ドラッグ中の見た目（面の強調・枠線の色・カーソル）を切り替える */
  private setSectionDragVisual(active: boolean): void {
    if (this.sectionPlane !== null) {
      this.sectionPlane.material.opacity = active ? SECTION_DRAG_OPACITY : SECTION_OPACITY;
    }
    if (this.sectionBorder !== null) {
      this.sectionBorder.material.color.set(SCENE_THEMES[this.theme].sectionBorder);
      if (active) this.sectionBorder.material.color.lerp(new THREE.Color(0xffffff), 0.5);
    }
    if (active) {
      this.hoverBox.visible = false;
      this.renderer.domElement.style.cursor = 'grabbing';
    } else {
      this.updateHover();
    }
  }

  private pickAtPointer(): number | null {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(this.activePickBoxes, false);
    if (hits.length === 0) return null;
    return hits[0].object.userData.index as number;
  }

  private updateHover(): void {
    if (!this.hoverEnabled) {
      this.hoverBox.visible = false;
      return;
    }
    const index = this.pickAtPointer();
    if (index !== null) {
      this.hoverBox.visible = true;
      this.hoverBox.position.copy(this.pickBoxes[index].position);
      this.renderer.domElement.style.cursor = 'pointer';
    } else {
      this.hoverBox.visible = false;
      // 断面面上ならドラッグ可能であること（カーソルで示す）
      this.renderer.domElement.style.cursor =
        this.sectionHitAtPointer() !== null ? 'grab' : 'default';
    }
  }

  /** ホバーハイライトの色を設定（現在のプレイヤーの目標色などに使う） */
  setHoverColor(color: THREE.Color): void {
    (this.hoverBox.material as THREE.MeshBasicMaterial).color.copy(color);
  }

  /** ピッキングとホバーの有効/無効（石を置ける 'selecting' フェーズのみ有効） */
  setInteractionEnabled(enabled: boolean): void {
    this.hoverEnabled = enabled;
    if (!enabled) {
      this.hoverBox.visible = false;
      this.renderer.domElement.style.cursor = 'default';
    }
  }

  // ---------- 断面（切り出し）表示 ----------
  // いずれも「表示専用」。ゲーム状態（石の種類・位置・確率・観測状態）には一切触れない。

  /**
   * 断面表示を開始・変更する。選択面から断面までの手前側の層を非表示にし、
   * 断面をハイライトする。
   * 注意: 視点は自動で移動しない（ユーザーの回転・ズームを維持する）。
   * 断面を見やすい位置へ動かす場合は aimAtSection() を明示的に呼ぶ。
   */
  setCutaway(face: Face, depth: number): void {
    this.cutaway = { face, depth };
    this.applyCutaway();
  }

  /** 全体表示に戻る（切り出しを解除する）。ゲーム状態・視点は変更しない */
  clearCutaway(): void {
    this.cutaway = null;
    this.applyCutaway();
  }

  /** 切り出し状態の現在値（null = 全体表示） */
  getCutaway(): { face: Face; depth: number } | null {
    return this.cutaway;
  }

  /**
   * 現在の断面を「選択面の外側から」見やすい位置へ視点を動かす（手動操作）。
   * 全体表示中（切り出しなし）は何もしない。
   */
  aimAtSection(): boolean {
    if (this.cutaway === null) return false;
    this.aimCameraToSection(this.cutaway.face, this.cutaway.depth);
    return true;
  }

  private applyCutaway(): void {
    this.rebuildGrid();
    this.updateStoneVisibility();
    this.updateActivePickBoxes();
    this.updateSectionHighlight();
  }

  /** 切り出し領域内の石メッシュを非表示にする（ゲーム状態はそのまま） */
  private updateStoneVisibility(): void {
    for (const [index, mesh] of this.stoneMeshes) {
      const hidden =
        this.cutaway !== null &&
        isCutawayCell(
          this.cutaway.face,
          this.cutaway.depth,
          indexOfX(index),
          indexOfY(index),
          indexOfZ(index),
        );
      mesh.group.visible = !hidden;
    }
  }

  /** ピッキング対象の更新（切り出し中は隠れた空きマスをクリックできないようにする） */
  private updateActivePickBoxes(): void {
    this.activePickBoxes = [];
    for (let i = 0; i < BOARD_CELLS; i++) {
      if (this.boardSnapshot[i] !== null) continue;
      if (
        this.cutaway !== null &&
        isCutawayCell(
          this.cutaway.face,
          this.cutaway.depth,
          indexOfX(i),
          indexOfY(i),
          indexOfZ(i),
        )
      ) {
        continue;
      }
      this.activePickBoxes.push(this.pickBoxes[i]);
    }
  }

  /** 断面のハイライト（半透明面＋枠線）を再構築する */
  private updateSectionHighlight(): void {
    for (const child of [...this.sectionGroup.children]) {
      this.sectionGroup.remove(child);
      if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      }
    }
    this.sectionPlane = null;
    this.sectionBorder = null;
    if (this.cutaway === null) {
      this.sectionGroup.visible = false;
      return;
    }
    this.sectionGroup.visible = true;

    const { face, depth } = this.cutaway;
    const info = FACE_INFO[face];
    const v = sectionFixedValue(face, depth);
    const center = new THREE.Vector3();
    if (info.fixed === 'x') center.set(v - HALF, 0, 0);
    else if (info.fixed === 'y') center.set(0, v - HALF, 0);
    else center.set(0, 0, v - HALF);
    const outward = new THREE.Vector3(...info.outward);
    const dragging = this.sectionDrag?.phase === 'active';

    // 半透明の断面（1 層 = 9 ユニットよりわずかに大きくして枠を見せる）
    const size = BOARD_SIZE - 1 + 0.5;
    const palette = SCENE_THEMES[this.theme];
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(size, size),
      new THREE.MeshBasicMaterial({
        color: palette.section,
        transparent: true,
        opacity: dragging ? SECTION_DRAG_OPACITY : SECTION_OPACITY,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    plane.position.copy(center);
    plane.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), outward);
    this.sectionGroup.add(plane);
    this.sectionPlane = plane;

    // 断面の枠線（外向き法線に直交する 2 方向で矩形を作る）
    const up =
      Math.abs(outward.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
    const u1 = new THREE.Vector3().crossVectors(outward, up).normalize();
    const u2 = new THREE.Vector3().crossVectors(outward, u1).normalize();
    const half = size / 2;
    const corners = [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
      [-1, -1],
    ].map(
      ([a, b]) =>
        center.clone().addScaledVector(u1, a * half).addScaledVector(u2, b * half),
    );
    const border = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(corners),
      new THREE.LineBasicMaterial({
        color: new THREE.Color(palette.sectionBorder).lerp(
          new THREE.Color(0xffffff),
          dragging ? 0.5 : 0,
        ),
      }),
    );
    this.sectionGroup.add(border);
    this.sectionBorder = border;
  }

  /** カメラを「選択面の外側から断面を見ている」位置へ調整する（計算は sectionCameraPose） */
  private aimCameraToSection(face: Face, depth: number): void {
    const { position, target } = sectionCameraPose(face, depth);
    this.camera.position.set(position[0], position[1], position[2]);
    this.controls.target.set(target[0], target[1], target[2]);
    this.controls.update();
  }

  private animate = (): void => {
    if (this.disposed) return;
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  };

  /** ゲーム状態に応じて石・ハイライト・ピッキング対象を更新する */
  sync(game: Game): void {
    const board = game.state.board;

    // 石のメッシュの更新（追加/削除/見た目）
    const seen = new Set<number>();
    for (let i = 0; i < BOARD_CELLS; i++) {
      const stone = board[i];
      if (stone === null) continue;
      seen.add(i);
      let mesh = this.stoneMeshes.get(i);
      if (!mesh) {
        const x = indexOfX(i);
        const y = indexOfY(i);
        const z = indexOfZ(i);
        const palette = SCENE_THEMES[this.theme];
        mesh = new StoneMesh(
          cellPosition(x, y, z),
          palette.shell,
          palette.shellOpacity,
        );
        this.scene.add(mesh.group);
        this.stoneMeshes.set(i, mesh);
      }
      mesh.update(stone.type, stone.observedColor);
    }
    for (const [index, mesh] of this.stoneMeshes) {
      if (!seen.has(index)) {
        this.scene.remove(mesh.group);
        mesh.dispose();
        this.stoneMeshes.delete(index);
      }
    }

    // ピッキング対象と切り出し中の石の可視性を更新（空きマスは 3D 直接クリックも可能）
    this.boardSnapshot = board;
    this.updateActivePickBoxes();
    this.updateStoneVisibility();

    this.updateWinHighlights(game);
  }

  private updateWinHighlights(game: Game): void {
    // 古いハイライトを削除
    for (const child of [...this.highlightGroup.children]) {
      this.highlightGroup.remove(child);
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      }
    }

    const lines = game.getWinLines();
    for (const { color, cells } of lines) {
      if (cells.length < 2) continue;
      const start = cellPosition(cells[0][0], cells[0][1], cells[0][2]);
      const end = cellPosition(cells[cells.length - 1][0], cells[cells.length - 1][1], cells[cells.length - 1][2]);
      const dir = new THREE.Vector3().subVectors(end, start);
      const length = dir.length();
      const radius = 0.12;
      const geo = new THREE.CylinderGeometry(radius, radius, length, 16);
      const mat = new THREE.MeshStandardMaterial({
        color: color === 'black' ? COLOR_BLACK : COLOR_WHITE,
        emissive: color === 'black' ? 0x333333 : 0x999999,
        roughness: 0.4,
      });
      const cylinder = new THREE.Mesh(geo, mat);
      // CylinderGeometry は +Y 方向を軸にするため、dir 方向へ回転させる
      cylinder.position.copy(new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5));
      const orientation = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        dir.clone().normalize(),
      );
      cylinder.quaternion.copy(orientation);
      this.highlightGroup.add(cylinder);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.renderer.setAnimationLoop(null);
    this.renderer.domElement.removeEventListener('pointerdown', this.handlePointerDown);
    this.renderer.domElement.removeEventListener('pointermove', this.handlePointerMove);
    this.renderer.domElement.removeEventListener('pointerup', this.handlePointerUp);
    this.renderer.domElement.removeEventListener('pointercancel', this.handlePointerUp);
    this.resizeObserver.disconnect();
    for (const mesh of this.stoneMeshes.values()) mesh.dispose();
    this.stoneMeshes.clear();
    if (this.gridLines !== null) {
      this.gridLines.geometry.dispose();
      (this.gridLines.material as THREE.Material).dispose();
      this.gridLines = null;
    }
    for (const child of this.sectionGroup.children) {
      if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      }
    }
    this.controls.dispose();
    this.renderer.dispose();
    if (this.renderer.domElement.parentNode === this.container) {
      this.container.removeChild(this.renderer.domElement);
    }
  }
}
